import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import { ArrowLeft, Package, RefreshCw, Search, X } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { ScrollArea } from "~/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useMaquilaAreas, useMaquilaInventory } from "~/hooks/maquila";
import { supabase } from "~/integrations/supabase/client";
import type { MaquilaArea, MaquilaInventory } from "~/types/maquila";

export const MaquilaInventoryPage = () => {
  const navigate = useNavigate();
  const [selectedAreaId, setSelectedAreaId] = useState<string | undefined>();
  const [selectedClientId, setSelectedClientId] = useState<
    string | undefined
  >();
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>(
    []
  );
  const [clientSearch, setClientSearch] = useState("");
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);

  // Fetch maquila clients
  useEffect(() => {
    const fetchClients = async () => {
      const { data } = await supabase
        .from("clients")
        .select("id, name")
        .eq("is_maquila_client", true)
        .order("name");
      if (data) setClients(data);
    };
    fetchClients();
  }, []);

  const { data: areas, isLoading: areasLoading } = useMaquilaAreas();
  const {
    data: inventory,
    isLoading: inventoryLoading,
    refetch,
  } = useMaquilaInventory(
    selectedAreaId ? { area_id: selectedAreaId } : undefined
  );

  const isLoading = areasLoading || inventoryLoading;

  // Filter clients by search
  const filteredClients = clients.filter((client) => {
    if (!clientSearch.trim()) return true;
    return client.name?.toLowerCase().includes(clientSearch.toLowerCase());
  });

  const selectedClient = clients.find((c) => c.id === selectedClientId);

  // Filter inventory by client and group by area
  const filteredInventory = selectedClientId
    ? inventory?.filter(
        (inv) => inv.maquila_item?.client?.id === selectedClientId
      )
    : inventory;

  const inventoryByArea = filteredInventory?.reduce(
    (acc, inv) => {
      const areaId = inv.maquila_area_id;
      if (!acc[areaId]) acc[areaId] = [];
      acc[areaId].push(inv);
      return acc;
    },
    {} as Record<string, MaquilaInventory[]>
  );

  const renderAreaCard = (area: MaquilaArea) => {
    const areaInventory = inventoryByArea?.[area.id] || [];
    const totalItems = areaInventory.length;
    const totalQuantity = areaInventory.reduce(
      (sum, inv) => sum + Number(inv.quantity),
      0
    );

    return (
      <Card key={area.id} className="overflow-hidden">
        <CardHeader className="bg-muted/50 pb-3">
          <CardTitle className="flex items-center justify-between text-base">
            <span>{area.name}</span>
            <span className="text-xs font-normal text-muted-foreground">
              {totalItems} materiales
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {areaInventory.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              <Package className="mr-2 h-4 w-4" />
              Sin materiales en esta area
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Material</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="text-right">Cantidad</TableHead>
                  <TableHead>Unidad</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {areaInventory.map((inv, index) => (
                  <TableRow
                    key={inv.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  >
                    <TableCell className="font-medium">
                      {inv.maquila_item?.name}
                      {inv.maquila_item?.code && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({inv.maquila_item.code})
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{inv.maquila_item?.client?.name}</TableCell>
                    <TableCell className="text-right font-mono">
                      {Number(inv.quantity).toLocaleString("es-CL")}
                    </TableCell>
                    <TableCell>{inv.maquila_item?.unit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="mx-auto w-full max-w-6xl p-6 lg:w-[80vw]">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/maquila")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              Inventario Maquila
            </h1>
            <p className="text-sm text-muted-foreground">
              Stock actual por area
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Popover open={clientPopoverOpen} onOpenChange={setClientPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-56 justify-between font-normal"
              >
                <span className="truncate">
                  {selectedClient?.name || "Todos los clientes"}
                </span>
                {selectedClientId ? (
                  <X
                    className="ml-2 h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedClientId(undefined);
                      setClientSearch("");
                    }}
                  />
                ) : (
                  <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-0" align="start">
              <div className="p-2">
                <Input
                  placeholder="Buscar cliente..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="h-8"
                />
              </div>
              <ScrollArea className="h-48">
                <div className="p-1">
                  <button
                    className="w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted"
                    onClick={() => {
                      setSelectedClientId(undefined);
                      setClientSearch("");
                      setClientPopoverOpen(false);
                    }}
                  >
                    Todos los clientes
                  </button>
                  {filteredClients.map((client) => (
                    <button
                      key={client.id}
                      className={`w-full rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted ${
                        selectedClientId === client.id ? "bg-muted" : ""
                      }`}
                      onClick={() => {
                        setSelectedClientId(client.id);
                        setClientSearch("");
                        setClientPopoverOpen(false);
                      }}
                    >
                      {client.name}
                    </button>
                  ))}
                  {filteredClients.length === 0 && clientSearch && (
                    <p className="px-2 py-4 text-center text-sm text-muted-foreground">
                      No se encontraron clientes
                    </p>
                  )}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
          <Select
            value={selectedAreaId || "all"}
            onValueChange={(v) =>
              setSelectedAreaId(v === "all" ? undefined : v)
            }
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filtrar por area" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las areas</SelectItem>
              {areas?.map((area) => (
                <SelectItem key={area.id} value={area.id}>
                  {area.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid gap-6">
          {selectedAreaId
            ? // Single area view
              areas
                ?.filter((a) => a.id === selectedAreaId)
                .map((area) => renderAreaCard(area))
            : // All areas view
              areas?.map((area) => renderAreaCard(area))}
        </div>
      )}
    </div>
  );
};
